import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import BusTracking from '../components/BusTracking';
import ConsoleApp from '../console-v3/ConsoleApp';
import LorryFleetConsole from './lorry/LorryFleetConsole';
import SubscriptionGate from '../components/SubscriptionGate';
import FamilyDashboard from '../components/family/FamilyDashboard';
import FamilySubscriptionGate from '../components/family/FamilySubscriptionGate';
import { useOrgSubscription } from '../services/useOrgSubscription';
import { isFamilyOrg, isLorryOrg, rawProductType } from './fleetConstants';

/** Routes fleet admins to bus tracking vs lorry fleet console vs the Trasse
 *  Families household console, by org productType. Wraps the chosen console in a
 *  subscription gate so enforcement is product-agnostic — households get their
 *  own gate, because the generic lockout screen offers a mock "pay" button. */
export default function FleetPortalRouter() {
  const { fleetOrgCode } = useAuth();
  const location = useLocation();
  const [productType, setProductType] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);

  const sub = useOrgSubscription(fleetOrgCode);

  useEffect(() => {
    if (!fleetOrgCode) {
      setProductType(null);
      setLoading(false);
      return;
    }
    const q = query(collection(db, 'organizations'), where('code', '==', fleetOrgCode), limit(1));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const d = snap.docs[0]?.data();
        setProductType(rawProductType(d?.productType as string | undefined) || 'FULL_FEATURE');
        setOrgName((d?.name as string) || fleetOrgCode);
        setLoading(false);
      },
      () => {
        setProductType('FULL_FEATURE');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [fleetOrgCode]);

  if (loading || sub.status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Sora, sans-serif', background: '#F3F8FF', color: '#6B7A8F' }}>
        Loading fleet portal…
      </div>
    );
  }

  // A household is not a fleet: /fleet belongs to the school and lorry consoles,
  // /families to Trasse Families. Send each product to its own address so a
  // bookmarked or shared link never lands anyone in the wrong console.
  const onFamilyPath = location.pathname === '/families' || location.pathname.startsWith('/families/');
  if (productType && isFamilyOrg(productType)) {
    if (!onFamilyPath) return <Navigate to="/families" replace />;
    return (
      <FamilySubscriptionGate sub={sub} orgCode={fleetOrgCode || ''} orgName={orgName}>
        <FamilyDashboard orgCode={fleetOrgCode || ''} orgName={orgName} sub={sub} />
      </FamilySubscriptionGate>
    );
  }
  if (onFamilyPath) return <Navigate to="/fleet" replace />;

  // School bus organisations get the v3 console (2026-10-23). The classic
  // dashboard stays at /fleet/classic so nothing it does is out of reach while
  // v3 settles. Lorry fleets keep their own console.
  const classic = location.pathname === '/fleet/classic' || location.pathname.startsWith('/fleet/classic/');
  let console: React.ReactNode;
  if (productType && isLorryOrg(productType)) {
    if (location.pathname !== '/fleet') return <Navigate to="/fleet" replace />;
    console = <LorryFleetConsole productType={productType} />;
  } else if (classic) {
    console = <BusTracking />;
  } else {
    console = <ConsoleApp />;
  }

  return (
    <SubscriptionGate sub={sub} orgCode={fleetOrgCode || ''} orgName={orgName}>
      {console}
    </SubscriptionGate>
  );
}
